import { useMemo } from 'react'

import { Option } from '../../../models/Option'
import { SelectList } from '../../common/SelectList'

export type FieldSelectListProps = {
    fieldType: string
    options: Option[]
    value?: string | string[] | undefined
    onChange?: (value: string | string[] | undefined) => void
    selectionLimit?: number
}

/**
 * Wrapper around {@link SelectList} to make it easier to consume as an input for select and
 * multiselect record fields.
 */
export function FieldSelectList({
    fieldType,
    options,
    value,
    onChange,
    selectionLimit,
}: FieldSelectListProps) {
    const items = useMemo(
        () => options.map((opt) => ({ id: opt.id!, title: opt.label! })),
        [options]
    )

    const selectedItems = useMemo(() => {
        const valueNormalized = Array.isArray(value) ? value : value === undefined ? [] : [value]

        return valueNormalized.map((id) => {
            const opt = options.find((opt) => opt.id === id)

            // Generally shouldn't happen when used properly as a controlled component.
            if (!opt) {
                throw new Error(`No option found for selected option id '${id}'`)
            }

            return {
                id: opt.id!,
                title: opt.label!,
            }
        })
    }, [value, options])

    const handleSelectItems = (selectedItems: typeof items) => {
        if (fieldType === 'select') {
            onChange?.(selectedItems[0]?.id)
        }
        else if (fieldType === 'multiselect') {
            onChange?.(selectedItems.map((item) => item.id!))
        }
    }

    return (
        <SelectList
            checkboxSide="left"
            selectionType={fieldType === 'select' ? 'single' : 'multi'}
            items={items}
            selectedItems={selectedItems}
            onSelectItems={handleSelectItems}
            selectionLimit={selectionLimit}
        />
    )
}
